// Staff Prelead read hooks. The staff surface is a read-only MONITOR of the
// pipeline (managers watch; the generator drives their own tracks in the portal
// console) — so the only mutation here is appending a manager note to the
// thread. Adding a note invalidates the whole prelead prefix.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { preleadsApi, type PreleadListQuery } from "@/api/leadGen/preleads";
import { leadsApi } from "@/api/leads";
import { preleadKeys } from "@/hooks/leadGen/preleadKeys";

export function usePreleads(query: PreleadListQuery) {
  return useQuery({
    queryKey: preleadKeys.list(query as Record<string, unknown>),
    queryFn: () => preleadsApi.list(query),
  });
}

export function usePreleadStats(query: PreleadListQuery) {
  return useQuery({
    queryKey: preleadKeys.stats(query as Record<string, unknown>),
    queryFn: () => preleadsApi.stats(query),
  });
}

/** Per-card "this pay week" counts that reconcile with the Earnings Review. */
export function useWindowedPreleadStats(leadGeneratorId?: string, weekOf?: string) {
  return useQuery({
    queryKey: preleadKeys.windowedStats({ leadGeneratorId, weekOf }),
    queryFn: () => preleadsApi.windowedStats(leadGeneratorId, weekOf),
  });
}

export function usePrelead(id: string | null) {
  return useQuery({
    queryKey: preleadKeys.detail(id ?? ""),
    queryFn: () => preleadsApi.get(id as string),
    enabled: !!id,
  });
}

/** Area A — the source prelead for a lead, via the Leads.Read endpoint (so lead
 *  handlers who lack LeadGen.View can read it). Returns the same PreleadDetailDto. */
export function useSourcePreleadForLead(leadId: string | null) {
  return useQuery({
    queryKey: preleadKeys.sourceForLead(leadId ?? ""),
    queryFn: () => leadsApi.sourcePrelead(leadId as string),
    enabled: !!leadId,
  });
}

/** Display-only per-prelead compensation breakdown (running total + line items). */
export function usePreleadEarnings(id: string | null) {
  return useQuery({
    queryKey: preleadKeys.earnings(id ?? ""),
    queryFn: () => preleadsApi.earnings(id as string),
    enabled: !!id,
  });
}

export function usePreleadActivity(id: string | null) {
  return useQuery({
    queryKey: preleadKeys.activity(id ?? ""),
    queryFn: () => preleadsApi.activity(id as string),
    enabled: !!id,
  });
}

export function usePreleadNotes(id: string | null) {
  return useQuery({
    queryKey: preleadKeys.notes(id ?? ""),
    queryFn: () => preleadsApi.notes(id as string),
    enabled: !!id,
  });
}

/** The unified read-only Files list (general attachments + NDA/CIM docs). */
export function usePreleadAttachments(id: string | null) {
  return useQuery({
    queryKey: preleadKeys.attachments(id ?? ""),
    queryFn: () => preleadsApi.attachments(id as string),
    enabled: !!id,
  });
}

export function useAddPreleadNote(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => preleadsApi.addNote(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: preleadKeys.all });
    },
  });
}

// --- staff prelead management (Area D) — each invalidates the whole prelead
// prefix (list + stats + detail + activity + notes + earnings). ---------------

/** Staff disqualify (LeadGen.Manage): flip → Disqualified + void the still-Pending
 *  escalation credit + write the reason note. Reason required, notes optional. */
export function useDisqualifyPrelead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; reason: string; notes?: string | null }) =>
      preleadsApi.disqualify(vars.id, vars.reason, vars.notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: preleadKeys.all });
    },
  });
}

export function useArchivePrelead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preleadsApi.archive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: preleadKeys.all });
    },
  });
}

export function useUnarchivePrelead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preleadsApi.unarchive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: preleadKeys.all });
    },
  });
}

/** Admin soft delete (LeadGen.Delete). */
export function useDeletePrelead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preleadsApi.softDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: preleadKeys.all });
    },
  });
}

/** Admin restore of a soft-deleted prelead (LeadGen.Delete). */
export function useRestorePrelead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => preleadsApi.restore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: preleadKeys.all });
    },
  });
}
